import axios from 'axios';
import { VIDEO_PLAYER_URL } from '../../constants';
import {
    getOrderedRenditions,
    formatImageUrl
} from '../media_util.js';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import styles from './video.module.scss';
import { useIntersection } from '../../hooks/useIntersection';


function Video({
    title,
    hrefUrl,
    imageUrl,
    videoUrl,
    width,
    post,
    className,
    shouldRender,
    toggleModal,
    trackPostClick,
    trackVideoView
}) {
    const hasMultilink = !!post.multilink;
    const [poster, setPoster] = useState(imageUrl);
    const [renditions, setRenditions] = useState([]);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [lastUpdatedTime, setLastUpdatedTime] = useState(0.0);
    const [totalTimePlayed, setTotalTimePlayed] = useState(0.0);
    const videoPlayerEl = useRef();
    const SVG_PATHS = {
        'play': 'M11,10 L18,13.74 18,22.28 11,26 M18,13.74 L26,18 26,18 18,22.28',
        'pause': 'M11,10 L17,10 17,26 11,26 M20,10 L26,10 26,26 20,26',
    };

    const { isIntersecting, elRef } = useIntersection({
        once: true,
        rootMargin: '300px',
        threshold: 0,
    });

    const handleVideoEnded = () => {
        if (videoPlayerEl.current) {
            videoPlayerEl.current.load();
            setIsVideoPlaying(false);
        }
    };

    const toggleVideo = () => {
        if (videoPlayerEl.current) {
            // eslint-disable-next-line max-len
            videoPlayerEl.current.paused ? videoPlayerEl.current.play() : videoPlayerEl.current.pause();
            setIsVideoPlaying(!isVideoPlaying);
        }
    };

    const onTimeUpdate = () => {
        if (videoPlayerEl.current) {
            const currentTime = videoPlayerEl.current.currentTime;
            const timeDelta = Math.abs(currentTime - lastUpdatedTime);
            // overall total time played
            // can be longer than video as it keeps increasing if looping
            const newTotalTimePlayed = parseFloat(Number(totalTimePlayed + timeDelta).toFixed(4));

            // time has progressed forward
            if (currentTime > 0 && timeDelta > 0) {
                // if JUST passing the threshold of 3 seconds total, send a video view event
                if (totalTimePlayed < 3 && newTotalTimePlayed >= 3) {
                    trackVideoView();
                }
                setTotalTimePlayed(newTotalTimePlayed);
            }

            setLastUpdatedTime(currentTime);
        }
    };

    useEffect(() => {
        let isSubscribed = true;
        const loadVideoElement = async() => {
            let video_poster = imageUrl;
            let video_renditions = [];
            try {
                // eslint-disable-next-line max-len
                const url = `${VIDEO_PLAYER_URL}/v1/data?transcoder_path=${videoUrl.split('.com')[1]}`;
                const response = await axios.get(url);
                video_renditions = getOrderedRenditions(width, response.data.renditions);
                video_poster = formatImageUrl(imageUrl || video_renditions[0].poster_url, width);
            } catch (e) {
                console.error(e);
            }
            if (!video_poster) {
                shouldRender(false);
            }
            if(isSubscribed) {
                setPoster(video_poster);
                setRenditions(video_renditions);
            }
        };

        if(isIntersecting) {
            loadVideoElement();
        }
        return () => isSubscribed = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIntersecting]);

    const videoEl = isIntersecting ?
        <video
            className={styles.videoFill}
            muted
            onEnded={() => handleVideoEnded()}
            playsInline
            poster={poster}
            preload='none'
            ref={videoPlayerEl}
            title={title}
            onTimeUpdate={() => onTimeUpdate()}
        >
            {renditions.map((source, index) => {
                return (<source key={index} src={source.url} type={source.content_type}/>);
            })}
        </video> : null;

    return (
        <>
            {hasMultilink ? (
                <a
                    ref={elRef}
                    onClick={() => {
                        trackPostClick();
                        toggleModal(post, videoEl);
                    }}
                    className={className}
                >
                    {videoEl}
                </a>
            ) : (
                <a ref={elRef} href={hrefUrl} onClick={trackPostClick} className={className}>
                    {videoEl}
                </a>
            )}
            <button
                className={styles.playButton}
                aria-live="assertive"
                aria-label="Play"
                onClick={() => toggleVideo()}
            >
                <svg width="100%" height="100%" viewBox="0 0 36 36">
                    <path d={isVideoPlaying ? SVG_PATHS['pause'] : SVG_PATHS['play']} />
                </svg>
            </button>
        </>
    );
}

Video.propTypes = {
    title: PropTypes.string.isRequired,
    hrefUrl: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    videoUrl: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    className: PropTypes.string,
    shouldRender: PropTypes.func,
    toggleModal: PropTypes.func,
};

export default Video;
